import { createContext, useContext, useState, ReactNode, useEffect, useRef } from "react";
import { useCartPersistence } from "@/hooks/useCartPersistence";
import { useCompanyPricing } from "@/hooks/useCompanyPricing";
import { MARKET } from "@/lib/market";
import { cartStorageKey } from "@/lib/marketScope";
import { resolveBRCart } from "@/lib/resolveBRCart";

export interface CartItem {
  id: string;
  productId: string;
  productType: "equipment" | "consumable";
  variantId?: string;
  variantName?: string;
  name: string;
  price: number;
  quantity: number;
  imageUrl?: string;
}

interface CartContextType {
  items: CartItem[];
  addItem: (item: Omit<CartItem, "id">) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  addItemsFromOrder: (orderItems: Array<{ productId: string; productType: "equipment" | "consumable"; name: string; price: number; quantity: number; imageUrl?: string }>) => void;
  itemCount: number;
  subtotal: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

// PT keeps the legacy key; BR gets its own cart so items never cross markets.
const CART_STORAGE_KEY = cartStorageKey(MARKET);

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem(CART_STORAGE_KEY);
      const rows: CartItem[] = saved ? JSON.parse(saved) : [];
      return MARKET === "br" ? rows.map(item => ({ ...item, price: 0 })) : rows;
    }
    return [];
  });

  // Use cart persistence hook for database sync
  useCartPersistence(items, setItems);

  // Re-price cart items whenever company custom prices are loaded/updated.
  // Fixes race condition where items added before useCompanyPricing resolved
  // (or restored from DB at stale base prices) keep the wrong unit price.
  const { companyPrices, isLoading: pricingLoading, getProductPrice, getVariantPrice } = useCompanyPricing();
  const lastRepricedSig = useRef<string>("");

  const brCartSignature = items.map(i => `${i.id}:${i.productId}:${i.variantId ?? ""}`).join("|");
  useEffect(() => {
    if (MARKET !== "br" || !items.length) return;
    let cancelled = false;
    resolveBRCart(items).then(resolved => {
      if (cancelled) return;
      setItems(current => current.map(item => {
        const base = resolved.find(row => row.id === item.id)?.price ?? 0;
        const price = item.variantId ? getVariantPrice(item.variantId, base).price : getProductPrice(item.productId, item.productType, base).price;
        return { ...item, price };
      }));
    }).catch(() => {
      if (!cancelled) setItems(current => current.map(item => ({ ...item, price: 0 })));
    });
    return () => { cancelled = true; };
  }, [brCartSignature, getProductPrice, getVariantPrice]);

  useEffect(() => {
    if (MARKET === "br") return;
    if (pricingLoading) return;
    if (!companyPrices || companyPrices.length === 0) return;

    // Build a stable signature so we only reprice when the price set changes
    const sig = companyPrices
      .map((p) => `${p.product_id}:${p.custom_price}`)
      .sort()
      .join("|");
    if (sig === lastRepricedSig.current) return;

    setItems((current) => {
      let changed = false;
      const next = current.map((item) => {
        // Variants are looked up by their own ID
        const result = item.variantId
          ? getVariantPrice(item.variantId, item.price)
          : getProductPrice(item.productId, item.productType, item.price);
        const newPrice = result.price;
        if (newPrice && Math.abs(newPrice - item.price) > 0.001) {
          changed = true;
          return { ...item, price: newPrice };
        }
        return item;
      });
      if (changed) {
        lastRepricedSig.current = sig;
        return next;
      }
      lastRepricedSig.current = sig;
      return current;
    });
  }, [companyPrices, pricingLoading, getProductPrice, getVariantPrice]);

  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (newItem: Omit<CartItem, "id">) => {
    if (MARKET === "br") newItem = { ...newItem, price: 0 };
    setItems((currentItems) => {
      // Match by productId AND variantId (or lack thereof)
      const existingItem = currentItems.find(
        (item) => 
          item.productId === newItem.productId && 
          item.productType === newItem.productType &&
          item.variantId === newItem.variantId
      );

      if (existingItem) {
        return currentItems.map((item) =>
          item.id === existingItem.id
            ? { ...item, quantity: item.quantity + newItem.quantity }
            : item
        );
      }

      return [...currentItems, { ...newItem, id: crypto.randomUUID() }];
    });
  };

  const removeItem = (id: string) => {
    setItems((currentItems) => currentItems.filter((item) => item.id !== id));
  };

  const updateQuantity = (id: string, quantity: number) => {
    if (quantity < 1) {
      removeItem(id);
      return;
    }
    setItems((currentItems) =>
      currentItems.map((item) => (item.id === id ? { ...item, quantity } : item))
    );
  };

  const clearCart = () => {
    setItems([]);
  };

  const addItemsFromOrder = (orderItems: Array<{ productId: string; productType: "equipment" | "consumable"; name: string; price: number; quantity: number; imageUrl?: string; variantId?: string; variantName?: string }>) => {
    setItems((currentItems) => {
      const newItems = [...currentItems];
      
      orderItems.forEach((orderItem) => {
        const existingItemIndex = newItems.findIndex(
          (item) => 
            item.productId === orderItem.productId && 
            item.productType === orderItem.productType &&
            item.variantId === orderItem.variantId
        );

        if (existingItemIndex >= 0) {
          newItems[existingItemIndex] = {
            ...newItems[existingItemIndex],
            quantity: newItems[existingItemIndex].quantity + orderItem.quantity,
          };
        } else {
          newItems.push({
            id: crypto.randomUUID(),
            productId: orderItem.productId,
            productType: orderItem.productType,
            variantId: orderItem.variantId,
            variantName: orderItem.variantName,
            name: orderItem.name,
            price: MARKET === "br" ? 0 : orderItem.price,
            quantity: orderItem.quantity,
            imageUrl: orderItem.imageUrl,
          });
        }
      });

      return newItems;
    });
  };

  const itemCount = items.reduce((total, item) => total + item.quantity, 0);
  const subtotal = items.reduce((total, item) => total + item.price * item.quantity, 0);

  return (
    <CartContext.Provider
      value={{ items, addItem, removeItem, updateQuantity, clearCart, addItemsFromOrder, itemCount, subtotal }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}
